import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Loader2, HardDrive, RefreshCw, Edit } from "lucide-react";
import { useTranslation } from "@/i18n";

export function AllUsersStorageTable() {
  const { t } = useTranslation();
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [newQuota, setNewQuota] = useState<string>("");

  const { data: storageData, isLoading, refetch } = trpc.adminUsers.getAllStorageUsage.useQuery();

  const updateQuotaMutation = trpc.adminUsers.updateQuota.useMutation({
    onSuccess: () => {
      toast.success(t.settings.settingsSaved || "Quota updated successfully");
      setEditingUserId(null);
      setNewQuota("");
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update quota");
    },
  });

  const handleEditQuota = (userId: number, currentQuota: string) => {
    setEditingUserId(userId);
    setNewQuota(currentQuota);
  };

  const handleSaveQuota = () => {
    if (!editingUserId || !newQuota) return;
    
    const quotaGB = parseFloat(newQuota);
    if (isNaN(quotaGB) || quotaGB <= 0) {
      toast.error("Please enter a valid quota value");
      return;
    }

    updateQuotaMutation.mutate({
      userId: editingUserId,
      quotaGB,
    });
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatGB = (gb: string | number): string => {
    const value = typeof gb === "string" ? parseFloat(gb) : gb;
    return `${value.toFixed(2)} GB`;
  };

  const calculatePercentage = (used: number, quota: string): number => {
    const quotaBytes = parseFloat(quota) * 1024 * 1024 * 1024;
    if (quotaBytes === 0) return 0;
    return Math.min(100, (used / quotaBytes) * 100);
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage >= 90) return "text-red-600 dark:text-red-400";
    if (percentage >= 75) return "text-yellow-600 dark:text-yellow-400";
    return "text-green-600 dark:text-green-400";
  };

  if (isLoading) {
    return (
      <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            {(t.settings as any).allUsersStorage || "All Users Storage"}
          </CardTitle>
          <CardDescription>
            {t.common.loading || "Loading..."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!storageData || storageData.length === 0) {
    return (
      <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            {(t.settings as any).allUsersStorage || "All Users Storage"}
          </CardTitle>
          <CardDescription>
            {(t.settings as any).noUsersFound || "No users found"}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5" />
                {(t.settings as any).allUsersStorage || "All Users Storage"}
              </CardTitle>
              <CardDescription>
                {(t.settings as any).allUsersStorageDescription || "Overview of storage usage for all users"}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t.common.refresh || "Refresh"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{(t.settings as any).userName || "User"}</TableHead>
                  <TableHead>{(t.settings as any).userEmail || "Email"}</TableHead>
                  <TableHead className="text-right">{(t.settings as any).storageUsed || "Used"}</TableHead>
                  <TableHead className="text-right">{(t.settings as any).storageQuota || "Quota"}</TableHead>
                  <TableHead className="text-right">{(t.settings as any).storagePercentage || "Usage %"}</TableHead>
                  <TableHead className="text-right">{t.common.actions || "Actions"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {storageData.map((user: any) => {
                  const percentage = calculatePercentage(user.usedBytes, user.quotaGB);
                  const usageColor = getUsageColor(percentage);

                  return (
                    <TableRow key={user.userId}>
                      <TableCell className="font-medium">{user.userName}</TableCell>
                      <TableCell className="text-muted-foreground">{user.userEmail}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatBytes(user.usedBytes)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatGB(user.quotaGB)}
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${usageColor}`}>
                        {percentage.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditQuota(user.userId, user.quotaGB)}
                          className="gap-2"
                        >
                          <Edit className="h-4 w-4" />
                          {t.common.edit || "Edit"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Summary Statistics */}
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">{(t.settings as any).totalUsers || "Total Users"}</p>
              <p className="text-2xl font-bold">{storageData.length}</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">{(t.settings as any).totalUsed || "Total Used"}</p>
              <p className="text-2xl font-bold">
                {formatBytes(storageData.reduce((sum: number, user: any) => sum + user.usedBytes, 0))}
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">{(t.settings as any).totalQuota || "Total Quota"}</p>
              <p className="text-2xl font-bold">
                {formatGB(storageData.reduce((sum: number, user: any) => sum + parseFloat(user.quotaGB), 0))}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Quota Dialog */}
      <Dialog open={editingUserId !== null} onOpenChange={(open) => !open && setEditingUserId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{(t.settings as any).editQuota || "Edit Storage Quota"}</DialogTitle>
            <DialogDescription>
              {(t.settings as any).editQuotaDescription || "Set the storage quota for this user in GB"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="quota">{(t.settings as any).storageQuota || "Quota (GB)"}</Label>
              <Input
                id="quota"
                type="number"
                step="0.01"
                min="0.01"
                value={newQuota}
                onChange={(e) => setNewQuota(e.target.value)}
                placeholder="50.00"
              />
              <p className="text-sm text-muted-foreground">
                {(t.settings as any).quotaHelp || "Enter the storage quota in gigabytes (e.g., 50.00)"}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingUserId(null);
                setNewQuota("");
              }}
            >
              {t.common.cancel || "Cancel"}
            </Button>
            <Button
              onClick={handleSaveQuota}
              disabled={updateQuotaMutation.isPending || !newQuota}
            >
              {updateQuotaMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t.common.save || "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}